import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ndisApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Download, ArrowLeft, FileSpreadsheet, Shield, CheckCircle2,
  AlertTriangle, LogOut
} from 'lucide-react';
import { MobileNav } from '../components/MobileNav';
import { LogoHome } from '../components/LogoHome';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_e3b3c70d-c72e-49dc-b41a-da6affdc6e46/artifacts/4w4rfom9_IMG_0777.PNG";

export default function NdisExportPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [period, setPeriod] = useState('30');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    loadSummary();
  }, [period]);

  const loadSummary = async () => {
    try {
      const res = await ndisApi.getExportSummary(parseInt(period));
      setSummary(res.data);
    } catch (err) {
      toast.error('Failed to load export summary');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const response = await ndisApi.exportCsv(params);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `visitex_ndis_export_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('NDIS export downloaded successfully');
    } catch (err) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const backRoute = user?.role === 'organisation' ? '/admin' : '/supervisor';

  const navItems = [
    { to: backRoute, label: 'Back to Dashboard', icon: <ArrowLeft className="h-5 w-5" /> },
    { to: '/ndis-export', label: 'NDIS Export', icon: <FileSpreadsheet className="h-5 w-5" />, active: true },
  ];

  return (
    <div className="min-h-screen bg-slate-900" data-testid="ndis-export-page">
      <MobileNav navItems={navItems} user={user} onLogout={handleLogout} />

      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-slate-950 border-r border-slate-800 z-50 hidden lg:flex lg:flex-col">
        <div className="p-6 pb-2 flex-shrink-0">
          <LogoHome className="h-16 w-auto mb-4" />
        </div>
        <div className="flex-1 overflow-y-auto px-6 pb-2">
          <nav className="space-y-1">
            <Link to={backRoute} className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <ArrowLeft className="h-5 w-5" />
              Back to Dashboard
            </Link>
            <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-teal-600/20 text-teal-400 border border-teal-600/30">
              <FileSpreadsheet className="h-5 w-5" />
              NDIS Export
            </div>
          </nav>
        </div>
        <div className="flex-shrink-0 p-6 border-t border-slate-800">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-full bg-teal-600 flex items-center justify-center text-white font-medium">
              {user?.name?.charAt(0)}
            </div>
            <div>
              <p className="text-white font-medium text-sm">{user?.name}</p>
              <p className="text-slate-500 text-xs capitalize">{user?.role}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" className="w-full border-slate-700 text-slate-300 hover:bg-slate-800" onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:pl-64">
        <div className="p-6 lg:p-8">
          <div className="mb-8">
            <h1 className="text-2xl lg:text-3xl font-bold text-white">NDIS Portal Export</h1>
            <p className="text-slate-400 mt-1">Export NDIS-compatible data for portal upload and compliance reporting</p>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-lg bg-teal-600/20">
                    <FileSpreadsheet className="h-5 w-5 text-teal-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{summary?.total_reports || 0}</p>
                    <p className="text-xs text-slate-400">Total Reports</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-lg bg-emerald-600/20">
                    <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{summary?.approved_reports || 0}</p>
                    <p className="text-xs text-slate-400">Approved</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-lg bg-blue-600/20">
                    <Shield className="h-5 w-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{summary?.ai_generated || 0}</p>
                    <p className="text-xs text-slate-400">AI Generated</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-lg bg-amber-600/20">
                    <Download className="h-5 w-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{summary?.export_ready || 0}</p>
                    <p className="text-xs text-slate-400">Export Ready</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Export Configuration */}
          <Card className="bg-slate-800/50 border-slate-700 mb-6">
            <CardHeader>
              <CardTitle className="text-white text-base">Export Configuration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <Label className="text-slate-300 text-sm">Period</Label>
                  <Select value={period} onValueChange={setPeriod}>
                    <SelectTrigger className="bg-slate-900 border-slate-700 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">Last 7 days</SelectItem>
                      <SelectItem value="30">Last 30 days</SelectItem>
                      <SelectItem value="90">Last 90 days</SelectItem>
                      <SelectItem value="365">Last Year</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300 text-sm">Status Filter</Label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="bg-slate-900 border-slate-700 text-white mt-1" data-testid="status-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="approved">Approved Only</SelectItem>
                      <SelectItem value="submitted">Submitted Only</SelectItem>
                      <SelectItem value="generated">AI Generated</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300 text-sm">From Date</Label>
                  <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                    className="bg-slate-900 border-slate-700 text-white mt-1" />
                </div>
                <div>
                  <Label className="text-slate-300 text-sm">To Date</Label>
                  <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                    className="bg-slate-900 border-slate-700 text-white mt-1" />
                </div>
              </div>

              <Button
                onClick={handleExport}
                disabled={exporting}
                className="bg-teal-600 hover:bg-teal-700 text-white"
                data-testid="export-csv-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                {exporting ? 'Exporting...' : 'Export NDIS CSV'}
              </Button>
            </CardContent>
          </Card>

          {/* Export Info */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white text-base">NDIS Export Format</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-400 text-sm mb-4">The exported CSV includes the following NDIS-compatible fields:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {[
                  'Report ID & Date', 'Report Type & Status', 'Participant Name & NDIS Number',
                  'Cultural Background', 'Support Worker Details', 'Shift Summary',
                  'Goals & Achievements', 'Behavioural Observations', 'Concerns/Disclosures',
                  'Medication Records', 'Compliance Score', 'AI Report Status'
                ].map((field) => (
                  <div key={field} className="flex items-center gap-2 text-sm text-slate-300">
                    <CheckCircle2 className="h-3.5 w-3.5 text-teal-400 flex-shrink-0" />
                    {field}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
